<template>
    <div class="subscription-payment-modal-container">
        <div v-if="paymentLoad || subscriptionLoad" id="preloader" class="preloader"></div>
        <base-modal mk="subscription-payment" :show.sync="show" :styles="paymentModalStyles">
            <div class="subscription-payment-modal-wrapper">
                <div class="payment-modal-header">
                    <p class="payment-modal-title">{{ upgradeTitle }}</p>
                    <div class="payment-modal-desc-wrapper">
                        <p class="payment-modal-desc">
                            {{ upgradeDesc }}
                        </p>
                        <div class="upgrade-reasons">
                            <div
                                v-for="(reason, indx) in UPGRADE_REASONS"
                                :key="indx"
                                class="upgrade-reason"
                            >
                                <img :src="URLS.icons.whiteCheck" alt="white-check" />
                                <p class="reason">
                                    {{ reason }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="payment-modal-content">
                    <SubscriptionSelector :role="role" />
                    <payment-methods :role="role">
                        <template #payment-actions>
                            <div class="payment-actions">
                                <button
                                    :style="ctaBtnStyle"
                                    :disabled="!favPayment"
                                    @click="subscribe"
                                    class="subscribe-btn"
                                >
                                    {{ ctaBtnText }}
                                </button>
                                <button @click="closeModal" class="cancel-btn">Cancel</button>
                            </div>
                        </template>
                    </payment-methods>
                </div>
            </div>
        </base-modal>
    </div>
</template>
<script>
    import { useSubscription } from '@/composables/features/subscriptions/useSubscriptions'
    import { usePaymentMethods } from '@/composables/features/payments-methods/usePayments'
    import useCheckScreen from '@/composables/useCheckScreen'
    import BaseModal from '@/composables/ui/useModal.js'
    import SubscriptionSelector from '@/components/subscriptions/SubscriptionSelector'
    import PaymentMethods from '@/components/payment-methods/PaymentMethods'

    const URLS = {
        icons: {
            whiteCheck: '/images/icons/checkmark-white.svg',
            yellowCheck: '/images/global/icon-yes-art.png',
            emptySelector: '/images/icons/selector-empty-white.svg',
            emptyUnselectableSelector: '/images/icons/selector-empty.svg',
            filledSelector: '/images/icons/selector-filled.svg',
            dropdownIcon: '/images/icons/down-arrow-gray.svg'
        },
        cards: {
            'American Express': '/images/icons/payment-details/cc-amex.svg',
            Visa: '/images/icons/payment-details/cc-visa.svg',
            MasterCard: '/images/icons/payment-details/cc-mastercard.svg',
            Discover: '/images/icons/payment-details/cc-discover.svg',
            UnionPay: '/images/icons/payment-details/UnionPay.svg',
            DinaCard: '/images/icons/payment-details/DinaCard.svg',
            'Diners Club': '/images/icons/payment-details/DinersClub.svg',
            BCCard: '/images/icons/payment-details/BCCard.svg',
            JCB: '/images/icons/payment-details/JSB.svg',
            others: '/images/icons/payment-details/cc-regular.svg'
        }
    }
    export default {
        name: 'SubscriptionPaymentModal',
        components: {
            BaseModal,
            SubscriptionSelector,
            PaymentMethods
        },
        props: {
            show: {
                type: Boolean,
                required: true
            },
            role: {
                type: String,
                required: true
            },
            selectedPlanName: {
                type: String,
                required: false
            },
            displayFeatureText: {
                type: Boolean,
                required: false,
                default: true
            }
        },
        emits: ['done', 'open', 'close'],
        setup(props, context) {
            const { isMobile } = useCheckScreen()

            const { favPayment, load: paymentLoad } = usePaymentMethods()
            const {
                load: subscriptionLoad,
                UPGRADE_REASONS,
                canTrial,
                subscribe
            } = useSubscription(props, context)

            return {
                UPGRADE_REASONS,
                isMobile,
                favPayment,
                paymentLoad,
                subscriptionLoad,
                canTrial,
                subscribe
            }
        },
        data: () => ({
            modalStyles: {
                modalWrapper: {
                    right: 'auto',
                    top: '7%'
                }
            }
        }),
        computed: {
            URLS: () => URLS,
            upgradeTitle: (self) =>
                self.canTrial ? 'Start Your 30-Day Free Trial' : 'Upgrade for Full Access',
            upgradeDesc: (self) => {
                const freeTrialText = self.displayFeatureText
                    ? 'This feature is not available in our free plan. Upgrade now and take advantage of our 30 day trial.'
                    : 'Upgrade now and take advantage of our 30 day trial.'

                return self.canTrial
                    ? freeTrialText
                    : 'Your 30-day trial is up. Select the plan that works for you. Keep your events and promotions running smoothly.'
            },
            ctaBtnText: (self) => (self.canTrial ? 'Start 30-Day Trial' : 'Get Full Access'),
            ctaBtnStyle: (self) =>
                !self.favPayment ? { background: '#464646', color: '#262626' } : {},
            paymentModalStyles: (self) =>
                self.isMobile
                    ? { modalWrapper: { right: 'auto', top: '20%' } }
                    : self.modalStyles
        },
        methods: {
            closeModal() {
                this.$emit('update:show', false)
            }
        }
    }
</script>
<style lang="scss" scoped>
    $mobileMax: 600px;

    @media screen and (max-width: $mobileMax) {
        .subscription-payment-modal-wrapper {
            margin-top: 74px;
            padding: 0px 16px 80px 16px !important;
            max-height: 100% !important;
            background: black !important;

            .payment-modal-header {
                .payment-modal-title {
                    font-size: 32px !important;
                }
                .payment-modal-desc-wrapper {
                    .upgrade-reasons {
                        justify-content: flex-start !important;
                        flex-wrap: wrap;

                        .upgrade-reason {
                            width: auto !important;
                        }
                    }
                }
            }
            .payment-modal-content {
                flex-direction: column !important;
                align-items: center !important;
            }
        }
    }

    p {
        margin-bottom: 0px;
    }
    button {
        border: none;
    }
    .subscription-payment-modal-wrapper {
        max-width: 968px;
        max-height: 100%;
        height: 100%;
        width: 100%;
        padding: 48px 80px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 48px;
        border-radius: 24px;
        background: #262626;
        margin-bottom: 80px;

        .payment-modal-header {
            display: flex;
            flex-direction: column;
            width: 100%;
            justify-content: center;
            align-items: flex-start;
            gap: 20px;

            .payment-modal-title {
                color: #f5f5f5;
                font-family: 'Instrument Sans';
                font-size: 44px;
                font-style: normal;
                font-weight: 500;
                line-height: 110%;
                letter-spacing: -1.76px;
            }

            .payment-modal-desc-wrapper {
                display: flex;
                flex-direction: column;
                width: 100%;
                justify-content: center;
                align-items: flex-start;
                gap: 8px;

                .payment-modal-desc {
                    color: #acacac;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                }

                .upgrade-reasons {
                    display: flex;
                    flex-direction: row;
                    width: 100%;
                    justify-content: center;
                    align-items: center;
                    gap: 24px;

                    .upgrade-reason {
                        display: flex;
                        flex-direction: row;
                        width: 100%;
                        justify-content: flex-start;
                        align-items: center;
                        gap: 8px;

                        img {
                            height: 20px;
                            width: 20px;
                            object-fit: cover;
                        }

                        .reason {
                            color: #acacac;
                            font-family: 'Instrument Sans';
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 125%;
                        }
                    }
                }
            }
        }
        .payment-modal-content {
            display: flex;
            flex-direction: row;
            width: 100%;
            justify-content: center;
            align-items: flex-start;
            gap: 48px;

            .payment-actions {
                display: flex;
                flex-direction: column;
                width: 100%;
                justify-content: center;
                align-items: center;
                gap: 24px;

                .subscribe-btn {
                    display: flex;
                    padding: 12px 32px;
                    justify-content: center;
                    align-items: center;
                    align-self: stretch;
                    border-radius: 120px;
                    background: #cdeb09;
                    color: black;
                    font-family: 'Instrument Sans';
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 133%;
                }
                .cancel-btn {
                    background: none;
                    color: #acacac;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                    text-decoration-line: underline;
                    align-self: stretch;
                }
            }
        }
    }
</style>
